class ProductCard extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.activeColour = ''

    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(this.onEntry.bind(this), {
      threshold: 0,
    })

    this.observer.observe(this)
  }

  setupListeners () {
    super.setupListeners()

    this.onMouseMove = this.onMouseMove.bind(this)
    this.listeners.add(this, 'pointermove', this.onMouseMove)

    this.handleGtm = this.handleGtm.bind(this)
    this.listeners.add(this, 'click', this.handleGtm)
  }

  onMouseMove (event) {
    if (!window.BAO.MediaBreakpoints.isMd) return

    const swatchTrigger = event.target.closest(
      '[data-product-card-el="swatch"]',
    )

    if (!swatchTrigger) {
      this.setActiveItem('')
    } else {
      this.setActiveItem(swatchTrigger.dataset.colour)
    }
  }

  setActiveItem (newColour) {
    if (this.activeColour === newColour) return

    if (this.activeColour !== '') {
      const currentActiveColour = this.querySelector(
        `[data-product-card-el="swatchImage"][data-colour="${this.activeColour}"]`,
      )

      if (currentActiveColour) {
        currentActiveColour.setAttribute('aria-hidden', true)
      }
    }

    this.activeColour = newColour

    const image = this.querySelector(
      `[data-product-card-el="swatchImage"][data-colour="${newColour}"]`,
    )

    if (image) {
      image.removeAttribute('aria-hidden')
    }
  }

  async onEntry ([entry], observer) {
    if (!entry.isIntersecting) return

    observer.unobserve(this)

    if (!this.dataset?.productUrl) return

    await this.fetchProductContent()
  }

  async fetchProductContent () {
    await window.BAO.sectionRenderer
      .fetchSingle('product-card-hover-content', this.dataset.productUrl)
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(res)

        const coloursContent = newDOM.querySelector(
          '[data-product-content-el="colours"]',
        )
        const coloursFooterContent = newDOM.querySelector(
          '[data-product-content-el="colours-footer"]',
        )
        const variantsContent = newDOM.querySelector(
          '[data-product-content-el="variants"]',
        )

        if (!coloursContent) return

        this.els.colours.element.outerHTML = coloursContent.innerHTML
        this.els['colours-footer'].element.outerHTML =
          coloursFooterContent.innerHTML
        this.els.variants.element.innerHTML = variantsContent.innerHTML
      })
      .catch((e) => {
        console.error(e)
      })
  }

  handleGtm (event) {
    const target = event.target
    const actionIsQuickAdd = target.closest('[data-gtm-event="quick-add"]')
    const placementIsCompleteTheLook = target.closest(
      '[data-gtm-event="complete-the-look"]',
    )
    const placementIsProductCarousel = target.closest(
      '[data-gtm-event="product-carousel-slide"]',
    )
    const placementIsRecentlyViewedProducts = target.closest(
      '[data-gtm-event="recently-viewed-products"]',
    )
    const placementIsRecommendedProducts = target.closest(
      '[data-gtm-event="recommended-products"]',
    )

    if (actionIsQuickAdd) {
      this.trackQuickAdd(target)
    } else if (placementIsCompleteTheLook) {
      this.trackCompleteTheLookClick()
    } else if (placementIsProductCarousel) {
      this.trackProductCarouselClick(target)
    } else if (placementIsRecentlyViewedProducts) {
      this.trackRecentlyViewedProductsClick()
    } else if (placementIsRecommendedProducts) {
      this.trackRecommendedProductsClick()
    } else {
      this.trackProductCardClick()
    }
  }

  trackQuickAdd (target) {
    try {
      const dataLayer = window.dataLayer || []
      const url = document.location.pathname
      const template = url.split('/')[1]

      dataLayer.push({
        event: 'quick_add',
        quickAdd: {
          template,
          url,
          item: {
            handle: this.productHandle,
            id: this.productId,
            title: this.productTitle,
            price: this.productPrice,
            currency: this.productCurrency,
            variant: target.innerText,
          },
        },
      })
    } catch (error) {
      console.error('Error pushing to dataLayer: ', error)
    }
  }

  trackCompleteTheLookClick () {
    try {
      const dataLayer = window.dataLayer || []
      const url = document.location.pathname

      dataLayer.push({
        event: 'complete_the_look_click',
        completeTheLookClick: {
          url,
          item: {
            handle: this.productHandle,
            id: this.productId,
            title: this.productTitle,
            price: this.productPrice,
            currency: this.productCurrency,
          },
        },
      })
    } catch (error) {
      console.error('Error pushing to dataLayer: ', error)
    }
  }

  trackProductCarouselClick (target) {
    try {
      const dataLayer = window.dataLayer || []
      const url = document.location.pathname
      const slide = target.closest('[data-gtm-event="product-carousel-slide"]')

      dataLayer.push({
        event: 'product_carousel_click',
        productCarouselClick: {
          url,
          click_index: slide.ariaLabel,
          item: {
            handle: this.productHandle,
            id: this.productId,
            title: this.productTitle,
            price: this.productPrice,
            currency: this.productCurrency,
          },
        },
      })
    } catch (error) {
      console.error('Error pushing to dataLayer: ', error)
    }
  }

  trackRecentlyViewedProductsClick () {
    try {
      const dataLayer = window.dataLayer || []
      const url = document.location.pathname

      dataLayer.push({
        event: 'recently_viewed_products_click',
        recentlyViewedProductsClick: {
          url,
          item: {
            handle: this.productHandle,
            id: this.productId,
            title: this.productTitle,
            price: this.productPrice,
            currency: this.productCurrency,
          },
        },
      })
    } catch (error) {
      console.error('Error pushing to dataLayer: ', error)
    }
  }

  trackRecommendedProductsClick () {
    try {
      const dataLayer = window.dataLayer || []
      const url = document.location.pathname

      dataLayer.push({
        event: 'recommended_products_click',
        recommendedProductsClick: {
          url,
          item: {
            handle: this.productHandle,
            id: this.productId,
            title: this.productTitle,
            price: this.productPrice,
            currency: this.productCurrency,
          },
        },
      })
    } catch (error) {
      console.error('Error pushing to dataLayer: ', error)
    }
  }

  trackProductCardClick () {
    try {
      const dataLayer = window.dataLayer || []
      const url = document.location.pathname
      const template = url.split('/')[1]

      dataLayer.push({
        event: 'product_card_click',
        productCardClick: {
          template,
          url,
          item: {
            handle: this.productHandle,
            id: this.productId,
            title: this.productTitle,
            price: this.productPrice,
            currency: this.productCurrency,
          },
        },
      })
    } catch (error) {
      console.error('Error pushing to dataLayer: ', error)
    }
  }

  get productId () {
    return this.dataset.id || ''
  }

  get productHandle () {
    return this.dataset.handle || ''
  }

  get productPrice () {
    return this.dataset.price || ''
  }

  get productCurrency () {
    return this.dataset.currency || ''
  }

  get productTitle () {
    return this.dataset.title || ''
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard)
}
